import React from 'react';
import { DashboardTableProps } from '../../types/common';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

export const DashboardTable: React.FC<DashboardTableProps> = ({
  columns,
  rows,
  sortKey,
  sortDir,
  onSort,
  onRowClick,
  striped = false,
  maxHeight = 'max-h-[600px]',
  loading = false,
  emptyState = 'No data available',
}) => {
  const SortIcon = ({ colKey }: { colKey: string }) => {
    if (sortKey !== colKey)
      return <ChevronsUpDown size={13} className="text-slate-300 ml-1 inline" />;
    return sortDir === 'asc'
      ? <ChevronUp size={13} className="text-blue-500 ml-1 inline" />
      : <ChevronDown size={13} className="text-blue-500 ml-1 inline" />;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Skeleton header */}
        <div className="grid px-4 py-3 border-b border-slate-200 bg-slate-50"
             style={{ gridTemplateColumns: columns.map(c => c.width || '1fr').join(' ') }}>
          {columns.map(c => (
            <div key={c.key} className="h-3.5 bg-slate-200 rounded animate-pulse w-2/3" />
          ))}
        </div>
        {[...Array(6)].map((_, i) => (
          <div key={i} className="flex gap-4 px-4 py-3.5 border-b border-slate-100">
            {columns.map(c => (
              <div key={c.key} className="flex-1 h-3.5 bg-slate-100 rounded animate-pulse" />
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex items-center justify-center h-48 text-sm text-slate-600">
        {emptyState}
      </div>
    );
  }

  return (
    <div className={`${maxHeight} overflow-auto rounded-xl border border-slate-200 shadow-sm`}>
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200 sticky top-0">
            {columns.map(col => (
              <th
                key={col.key}
                onClick={() => col.sortable && onSort?.(col.key)}
                className={`
                  px-4 py-3 text-left
                  text-[11px] font-semibold uppercase tracking-wide text-slate-500
                  whitespace-nowrap select-none
                  ${col.sortable ? 'cursor-pointer hover:text-slate-800 hover:bg-slate-100' : ''}
                  ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'}
                  ${col.sticky ? 'sticky left-0 z-10 bg-slate-50' : ''}
                `}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.header}
                {col.sortable && <SortIcon colKey={col.key} />}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-slate-100">
          {rows.map((row, i) => (
            <tr
              key={i}
              onClick={() => onRowClick?.(row)}
              className={`
                transition-colors duration-75
                ${onRowClick ? 'cursor-pointer hover:bg-blue-50/40' : ''}
                ${striped && i % 2 === 1 ? 'bg-slate-50/60' : 'bg-white'}
              `}
            >
              {columns.map(col => (
                <td
                  key={col.key}
                  className={`
                    px-4 py-3 text-sm text-slate-700
                    ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'}
                    ${col.sticky ? 'sticky left-0 z-10 bg-inherit' : ''}
                  `}
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DashboardTable;
